import React, { useState, useEffect, useCallback } from 'react';
import { 
    FaSearch, FaTimes, FaBuilding, FaGlobe, FaCalendarAlt, 
    FaExternalLinkAlt, FaAward, FaChevronLeft, FaChevronRight, FaDollarSign 
} from 'react-icons/fa';
import grantService from '../../services/grantService';
import './GrantsModal.css';

const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Función para parsear fechas en español (ej: "15 de mayo de 2026")
const parseSpanishDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    try {
        // Si ya es ISO o formato estándar
        if (!isNaN(Date.parse(dateStr))) return new Date(dateStr);

        const monthsMap = {
            'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
            'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
        };

        const parts = dateStr.toLowerCase().split(' ');
        // Esperado: ["15", "de", "mayo", "de", "2026"]
        const day = parseInt(parts[0]);
        const monthName = parts[2];
        const year = parseInt(parts[4]);

        if (!isNaN(day) && monthsMap[monthName] !== undefined && !isNaN(year)) {
            return new Date(year, monthsMap[monthName], day);
        }
        return null;
    } catch (e) {
        return null;
    }
};

const GrantsModal = ({ isOpen, onClose, token }) => {
    const [grants, setGrants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalGrants, setTotalGrants] = useState(0);
    const [selectedDay, setSelectedDay] = useState(null);
    const [showAll, setShowAll] = useState(false);

    // Estado del Calendario
    const [viewDate, setViewDate] = useState(new Date());
    
    const loadGrants = useCallback(async () => {
        try {
            setLoading(true);
            const year = viewDate.getFullYear();
            const month = viewDate.getMonth();
            const firstDay = new Date(year, month, 1).toISOString();
            const lastDay = new Date(year, month + 1, 0).toISOString();

            const data = await grantService.getGrants({
                query: searchQuery,
                startDate: showAll ? undefined : firstDay,
                endDate: showAll ? undefined : lastDay,
                page
            }, token);
            
            setGrants(data.grants || []);
            setTotalGrants(data.total || 0);
        } catch (err) {
            console.error('Error loading grants in modal:', err);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, viewDate, page, token, showAll]);

    // Referencia para el input de búsqueda
    const searchInputRef = React.useRef(null);

    const resetFilters = () => {
        setSearchQuery('');
        setSelectedDay(null);
        setViewDate(new Date());
        setShowAll(true);
        setPage(1);
    };

    useEffect(() => {
        if (isOpen) {
            loadGrants();
        }
    }, [isOpen, loadGrants]);

    useEffect(() => {
        if (isOpen) {
            // Enfocar el input automáticamente al abrir
            setTimeout(() => {
                if (searchInputRef.current) searchInputRef.current.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadGrants();
    };

    const renderCalendar = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        // Ajustar offset para que empiece en Lunes (0=Dom, 1=Lun...)
        const startOffset = (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1);
        const cells = Array(startOffset).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

        const prevMonth = () => { setViewDate(new Date(year, month - 1, 1)); setSelectedDay(null); setShowAll(false); };
        const nextMonth = () => { setViewDate(new Date(year, month + 1, 1)); setSelectedDay(null); setShowAll(false); };

        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);

        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

        const handleMonthChange = (e) => {
            setViewDate(new Date(year, parseInt(e.target.value), 1));
            setSelectedDay(null);
            setShowAll(false);
        };

        const handleYearChange = (e) => {
            setViewDate(new Date(parseInt(e.target.value), month, 1));
            setSelectedDay(null);
            setShowAll(false);
        };

        // Días con convocatorias
        const startDays = new Set();
        const deadlineDays = new Set();

        grants.forEach(g => {
            const startDate = parseSpanishDate(g.fecha_inicio);
            const deadlineDate = parseSpanishDate(g.fecha_limite);

            if (startDate && startDate.getMonth() === month && startDate.getFullYear() === year) {
                startDays.add(startDate.getDate());
            }
            if (deadlineDate && deadlineDate.getMonth() === month && deadlineDate.getFullYear() === year) {
                deadlineDays.add(deadlineDate.getDate());
            }
        });

        return (
            <div className="gm-calendar">
                <div className="gm-cal-header">
                    <button className="gm-cal-nav" onClick={prevMonth}><FaChevronLeft /></button>
                    <div className="gm-cal-selectors">
                        <select className="gm-cal-select" value={month} onChange={handleMonthChange}>
                            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                        </select>
                        <select className="gm-cal-select" value={year} onChange={handleYearChange}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <button className="gm-cal-nav" onClick={nextMonth}><FaChevronRight /></button>
                </div>
                <div className="gm-cal-grid">
                    {DAYS.map((d, i) => (
                        <span key={i} className="gm-cal-dow">{d}</span>
                    ))}
                    {cells.map((day, i) => {
                        const cellDate = day ? new Date(year, month, day) : null;
                        const isToday = day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear();
                        const isPast = cellDate && cellDate < todayDate;
                        const isStart = day && startDays.has(day);
                        const isDeadline = day && deadlineDays.has(day);
                        const isSelected = day === selectedDay;

                        return (
                            <span
                                key={i}
                                className={`gm-cal-day ${isToday ? 'today' : ''} ${isPast ? 'past' : ''} ${isStart ? 'is-start' : ''} ${isDeadline ? 'is-deadline' : ''} ${isSelected ? 'selected' : ''} ${!day ? 'empty' : ''}`}
                                title={isStart ? 'Inicio de convocatoria' : isDeadline ? 'Fecha límite' : ''}
                                onClick={() => day && !isPast && setSelectedDay(day === selectedDay ? null : day)}
                                style={{ cursor: day && !isPast ? 'pointer' : 'default' }}
                            >
                                {day || ''}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="gm-page-overlay" onClick={onClose}>
            <div className="gm-page-container" onClick={e => e.stopPropagation()}>
                {/* ── HEADER ── */}
                <header className="gm-header">
                    <div className="gm-header-inner">
                        <button className="gm-back-btn" onClick={onClose}>
                            <FaChevronLeft /> Regresar
                        </button>
                        
                        <div className="gm-logo">
                            <div className="gm-logo-main">
                                <span className="gm-logo-highlight">Eco</span>ScienceInnova
                            </div>
                            <div className="gm-logo-sub">Grants Explorer</div>
                        </div>

                        <form className="gm-header-search" onSubmit={handleSearch}>
                            <FaSearch className="gm-header-search-icon" />
                            <input
                                ref={searchInputRef}
                                className="gm-header-search-input"
                                placeholder="Busca financiamiento en lenguaje natural (ej: 'grants para cáncer en LATAM')..."
                                value={searchQuery}
                                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                            />
                        </form>
                        <button className="gm-close-main" onClick={onClose} aria-label="Cerrar">
                            <FaTimes />
                        </button>
                    </div>
                </header>

                {/* ── TITLE BAR ── */}
                <div className="gm-title-bar">
                    <div className="gm-title-bar-inner">
                        <div>
                            <h1 className="gm-page-title">Convocatorias Científicas</h1>
                            <p className="gm-page-subtitle">Explora oportunidades de financiamiento y becas de investigación.</p>
                        </div>
                    </div>
                </div>

                {/* ── BODY ── */}
                <div className="gm-body">
                    <aside className="gm-sidebar">
                        {renderCalendar()}

                        <div className="gm-stats-card">
                            <div className="gm-stats-header">
                                <span className="gm-stats-dot">●</span>
                                <span className="gm-stats-label">Resumen de Actividad</span>
                                <button className="gm-btn-all" onClick={resetFilters}>Ver Todo</button>
                            </div>

                            <div className="gm-summary-period">
                                <FaCalendarAlt className="gm-summary-icon" />
                                <span>{selectedDay ? `${selectedDay} de ` : ''}{MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
                            </div>

                            <div className="gm-stats-grid">
                                <div className="gm-stat-item is-start">
                                    <div className="gm-stat-value">
                                        {grants.filter(g => {
                                            const d = parseSpanishDate(g.fecha_inicio);
                                            return d && d.getMonth() === viewDate.getMonth() && d.getFullYear() === viewDate.getFullYear() && (!selectedDay || d.getDate() === selectedDay);
                                        }).length}
                                    </div>
                                    <div className="gm-stat-label">Aperturas</div>
                                </div>
                                <div className="gm-stat-item is-deadline">
                                    <div className="gm-stat-value">
                                        {grants.filter(g => {
                                            const d = parseSpanishDate(g.fecha_limite);
                                            return d && d.getMonth() === viewDate.getMonth() && d.getFullYear() === viewDate.getFullYear() && (!selectedDay || d.getDate() === selectedDay);
                                        }).length}
                                    </div>
                                    <div className="gm-stat-label">Cierres</div>
                                </div>
                            </div>

                            <div className="gm-stat-total">
                                <span>Total vigentes en vista:</span>
                                <strong>{grants.filter(grant => {
                                    const dEnd = parseSpanishDate(grant.fecha_limite);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return !dEnd || dEnd >= today;
                                }).length}</strong>
                            </div>
                            
                            <p className="gm-stats-hint">Usa el calendario para filtrar por días específicos de interés.</p>
                        </div>
                    </aside>

                    <main className="gm-list-section">
                        {loading ? (
                            <div className="gm-loading">
                                <div className="gm-spinner"></div>
                                <p>Buscando convocatorias...</p>
                            </div>
                        ) : grants.length === 0 ? (
                            <div className="gm-empty">No se encontraron convocatorias para este periodo o búsqueda.</div>
                        ) : (
                            <div className="gm-list">
                                {grants.filter(grant => {
                                    const dStart = parseSpanishDate(grant.fecha_inicio);
                                    const dEnd = parseSpanishDate(grant.fecha_limite);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);

                                    // 1. Filtrar por fecha pasada (solo mostrar vigentes)
                                    if (dEnd && dEnd < today) return false;

                                    // 2. Definir periodos de vista
                                    const vMonth = viewDate.getMonth();
                                    const vYear = viewDate.getFullYear();

                                    // 3. Filtrar estrictamente por mes y año seleccionado (solo si NO estamos en "Ver Todo")
                                    if (!showAll) {
                                        const isMonthMatch = (dStart && dStart.getMonth() === vMonth && dStart.getFullYear() === vYear) ||
                                                         (dEnd && dEnd.getMonth() === vMonth && dEnd.getFullYear() === vYear);
                                        
                                        if (!isMonthMatch) return false;
                                    }

                                    // 4. Si hay un día seleccionado, filtrar por ese día exacto
                                    if (selectedDay) {
                                        const isDayMatch = (dStart && dStart.getDate() === selectedDay && dStart.getMonth() === vMonth) ||
                                                           (dEnd && dEnd.getDate() === selectedDay && dEnd.getMonth() === vMonth);
                                        return isDayMatch;
                                    }

                                    return true;
                                }).map(grant => (
                                    <div key={grant.id} className="gm-grant-card">
                                        <div className="gm-grant-img-wrap">
                                            <div className="gm-grant-placeholder">
                                                <FaBuilding />
                                            </div>
                                        </div>
                                        <div className="gm-grant-body">
                                            <div className="gm-grant-meta-top">
                                                <span className={`gm-badge ${grant.area === 'salud' ? 'salud' : ''}`}>
                                                    {grant.area || 'GRANT'}
                                                </span>
                                                <span className="gm-grant-date">
                                                    <FaCalendarAlt /> 
                                                    {grant.fecha_inicio && (
                                                        <span className="gm-date-opening">
                                                            Apertura: <span className="gm-val">{grant.fecha_inicio}</span>
                                                        </span>
                                                    )}
                                                    {grant.fecha_limite && (
                                                        <span className="gm-date-deadline">
                                                            Cierre: <span className="gm-val">{grant.fecha_limite}</span>
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            <h3 className="gm-grant-title">{grant.nombre}</h3>
                                            <p className="gm-grant-desc">{grant.descripcion}</p>
                                            
                                            <div className="gm-grant-details">
                                                <div className="gm-detail">
                                                    <FaGlobe /> {grant.pais_region || 'Internacional'}
                                                </div>
                                                <div className="gm-detail">
                                                    <FaDollarSign /> <span className="gm-monto-val">{grant.monto || 'Ver bases'}</span>
                                                </div>
                                            </div>

                                            <a href={grant.link} target="_blank" rel="noopener noreferrer" className="gm-btn-apply">
                                                Ver Convocatoria <FaExternalLinkAlt />
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="gm-pagination">
                            <button 
                                className="gm-page-btn" 
                                disabled={page === 1} 
                                onClick={() => setPage(page - 1)}
                            >
                                <FaChevronLeft />
                            </button>
                            <span className="gm-page-info">Página {page}</span>
                            <button 
                                className="gm-page-btn" 
                                disabled={grants.length < 10} 
                                onClick={() => setPage(page + 1)}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default GrantsModal;
